import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";
import { trackEvent } from "@/utils/tagManagerUtils";

const CLAUDE_INSTALL_URL =
  "https://claude.ai/settings/connectors?add=https%3A%2F%2Fapp.upfixe.com%2Fmcp";
const CHATGPT_INSTALL_URL = "https://chatgpt.com/settings/connectors";

const claudeDesktopSnippet = `{
  "mcpServers": {
    "upfixe-ads": {
      "url": "https://app.upfixe.com/mcp"
    }
  }
}`;

const tools = [
  {
    name: "analyze_company",
    purpose:
      "Scrape a company's homepage and extract their value proposition (target customer, key benefits, tone). Used to seed campaign creation.",
  },
  {
    name: "analyze_landing_page",
    purpose:
      "Scrape a specific landing page and identify the keywords + search intent it should target.",
  },
  {
    name: "get_keyword_suggestions",
    purpose:
      "Generate keyword candidates from Google's Keyword Planner, filtered to ones affordable on your daily budget. Returns search volume and CPC estimates.",
  },
  {
    name: "list_brands",
    purpose: "List the brands (companies/products) you've set up in Upfixe.",
  },
  {
    name: "create_brand",
    purpose:
      "Add a new brand to your Upfixe account — required before creating campaigns for it.",
  },
  {
    name: "list_campaigns",
    purpose:
      "List your existing Google Ads campaigns with status, budget, and recent metrics.",
  },
  {
    name: "get_campaign_performance",
    purpose:
      "Pull live CTR, CPC, conversions, spend for a campaign over a date range.",
  },
  {
    name: "get_value_prop_doc",
    purpose: "Read the saved value-prop document for one of your brands.",
  },
  {
    name: "update_value_prop_doc",
    purpose:
      "Edit your brand's value-prop document — affects future campaign generation.",
  },
  {
    name: "create_campaign_draft",
    purpose:
      "Generate a complete Google Ads campaign as a paused draft. Returns a direct deep-link to review in Google Ads before going live. This is the default creation path.",
  },
  {
    name: "create_campaign_live",
    purpose:
      "Create a campaign as live (running). Requires explicit confirm: true parameter.",
  },
  {
    name: "publish_campaign",
    purpose:
      "Publish a previously-drafted paused campaign. Requires explicit confirm: true.",
  },
  {
    name: "publish_campaign_to_microsoft_ads",
    purpose:
      "Publish a campaign directly to Microsoft Advertising (Bing Ads) as PAUSED with standard decimal budgeting and OwnedAndOperatedOnly network targeting.",
  },
  {
    name: "create_bing_campaign",
    purpose:
      "Create a search campaign directly in Microsoft Advertising with specified daily budget.",
  },
  {
    name: "get_bing_campaign_performance",
    purpose:
      "Fetch live CTR, CPC, conversions, and spend metrics directly from Microsoft Advertising.",
  },
  {
    name: "update_bing_campaign_status",
    purpose:
      "Safely pause or re-activate a Microsoft Advertising campaign (Paused / Active).",
  },
  {
    name: "pause_campaign",
    purpose: "Pause a currently-running campaign. Requires confirmation.",
  },
  {
    name: "resume_campaign",
    purpose: "Resume a paused campaign. Requires confirmation.",
  },
  {
    name: "add_negative_keywords",
    purpose: "Add negative keywords at campaign or ad group level.",
  },
];

const Section = ({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) => (
  <section id={id} className="scroll-mt-24">
    <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">{title}</h2>
    <div className="space-y-4 text-foreground/90">{children}</div>
  </section>
);

const CodeBlock = ({ children }: { children: React.ReactNode }) => (
  <pre className="bg-muted text-foreground text-sm font-mono rounded-md p-4 overflow-x-auto border border-border">
    <code>{children}</code>
  </pre>
);

const DocsMcp = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackEvent("mcp_docs_view", { path: "/docs/mcp" });
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Upfixe MCP Docs — Install in Claude, ChatGPT & Perplexity</title>
        <meta
          name="description"
          content="Installation guide for the Upfixe Google Ads MCP server. Setup steps for Claude Desktop, claude.ai, ChatGPT Connectors and MCP Inspector, plus the full tool list."
        />
        <meta property="og:title" content="Upfixe MCP Docs" />
        <meta
          property="og:description"
          content="Install the Upfixe Google Ads MCP server in Claude, ChatGPT or Perplexity."
        />
        <link rel="canonical" href="https://upfixe.com/docs/mcp" />
      </Helmet>

      <Header />

      <main className="flex-grow">
        <div className="container mx-auto max-w-3xl px-4 py-16 md:py-20">
          <div className="mb-12 pb-8 border-b border-border">
            <p className="text-sm text-primary font-medium mb-2">Docs Â· MCP server</p>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground tracking-tight">
              Install the Upfixe MCP
            </h1>
            <p className="mt-4 text-lg text-muted-foreground">
              Connect Upfixe to your AI client in under a minute. The server
              endpoint is the same for all clients:
            </p>
            <CodeBlock>https://app.upfixe.com/mcp</CodeBlock>
            <p className="mt-3 text-sm text-muted-foreground">
              You'll be prompted to sign in with the Google account that owns
              your Google Ads. Don't have an account yet?{" "}
              <Link to="/mcp" className="text-primary hover:underline">
                Start a 7-day free trial â†’
              </Link>
            </p>
          </div>

          <div className="space-y-14">
            <Section id="pricing" title="Pricing">
              <p>Free 7-day trial, no credit card required. After trial:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <strong>Solo</strong> — $99/month — 1 brand, web app + MCP
                  access
                </li>
                <li>
                  <strong>Agency</strong> — $249/month — 5 brands, web app +
                  MCP access
                </li>
                <li>
                  <strong>Studio</strong> — $599/month — 25 brands, web app +
                  MCP access
                </li>
              </ul>
              <p>
                All tiers include the same MCP server and the same tools. The
                only difference is how many brands / Google Ads accounts you
                can connect. See{" "}
                <Link to="/pricing" className="text-primary hover:underline">
                  upfixe.com/pricing
                </Link>{" "}
                for full plan details.
              </p>
            </Section>

            <Section id="claude-desktop" title="Claude Desktop">
              <p>
                Open your{" "}
                <code className="px-1.5 py-0.5 rounded bg-muted text-sm font-mono">
                  claude_desktop_config.json
                </code>{" "}
                (Settings â†’ Developer â†’ Edit Config) and add an entry under{" "}
                <code className="px-1.5 py-0.5 rounded bg-muted text-sm font-mono">
                  mcpServers
                </code>
                :
              </p>
              <CodeBlock>{claudeDesktopSnippet}</CodeBlock>
              <p>
                Save the file and restart Claude Desktop. The Upfixe tools will
                appear in the tool picker at the bottom of the chat.
              </p>
            </Section>

            <Section id="claude-ai" title="claude.ai (web)">
              <ol className="list-decimal pl-5 space-y-2">
                <li>Open Settings â†’ Connectors â†’ Add custom connector.</li>
                <li>
                  Paste the server URL:{" "}
                  <code className="px-1.5 py-0.5 rounded bg-muted text-sm font-mono">
                    https://app.upfixe.com/mcp
                  </code>
                </li>
                <li>Click "Connect" and complete the OAuth sign-in.</li>
                <li>Toggle the Upfixe connector on in any chat to use it.</li>
              </ol>
            </Section>

            <Section id="chatgpt" title="ChatGPT Connectors">
              <ol className="list-decimal pl-5 space-y-2">
                <li>Open Settings â†’ Connectors â†’ Create.</li>
                <li>
                  Paste the server URL:{" "}
                  <code className="px-1.5 py-0.5 rounded bg-muted text-sm font-mono">
                    https://app.upfixe.com/mcp
                  </code>
                </li>
                <li>Authorize and pin the connector to your workspace.</li>
              </ol>
              <p className="text-sm text-muted-foreground bg-muted/40 border border-border rounded-md p-4">
                <strong className="text-foreground">Note:</strong> ChatGPT
                currently restricts MCP write access to Business, Enterprise and
                Edu plans. This is OpenAI's limitation, not ours. Claude and
                Perplexity users have full access on any plan.
              </p>
            </Section>

            <Section id="mcp-inspector" title="MCP Inspector (for developers)">
              <p>
                For testing or building agents against the Upfixe server, point
                the MCP Inspector at our endpoint:
              </p>
              <CodeBlock>npx @modelcontextprotocol/inspector https://app.upfixe.com/mcp</CodeBlock>
              <p>
                Authenticate when prompted and you'll get the full tool list
                with live request/response inspection.
              </p>
            </Section>

            <Section id="tool-list" title="Tool list">
              <p>The server exposes 27 tools. The 15 most-used are listed below.</p>
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-md p-4 text-sm text-foreground">
                <strong>⚠️ Confirmation required for every write.</strong> Every
                write tool (<code className="font-mono">create_*</code>,{" "}
                <code className="font-mono">update_*</code>,{" "}
                <code className="font-mono">pause_*</code>,{" "}
                <code className="font-mono">resume_*</code>,{" "}
                <code className="font-mono">publish_*</code>,{" "}
                <code className="font-mono">add_negative_keywords</code>)
                requires explicit confirmation in chat before executing.
                Campaigns are created as paused by default — nothing goes live
                or modifies your Google Ads account without your explicit
                consent.
              </div>
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 font-semibold text-foreground">
                        Tool
                      </th>
                      <th className="text-left p-3 font-semibold text-foreground">
                        Purpose
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tools.map((t, i) => (
                      <tr
                        key={t.name}
                        className={i % 2 === 0 ? "bg-background" : "bg-muted/20"}
                      >
                        <td className="p-3 font-mono text-xs text-primary align-top whitespace-nowrap">
                          {t.name}
                        </td>
                        <td className="p-3 text-muted-foreground">{t.purpose}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-muted-foreground italic">
                Plus 12 additional tools for account setup, budget updates,
                keyword forecasts, and brand/account selection. The
                authoritative list is whatever your AI client shows after
                authenticating — tools are advertised live via the MCP protocol
                on connect.
              </p>
            </Section>

            <Section id="examples" title="Example chat flows">
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-foreground mb-2">
                    Drafting a small-budget campaign
                  </h3>
                  <CodeBlock>{`You: Draft me a $50/day plumber campaign in Austin, TX.

Upfixe: Drafted "Austin Plumbers – $50/day" with:
  • 2 ad groups (Emergency, Drain Cleaning)
  • Exact + phrase match
  • Maximize Conversions bidding
  • 24 keywords, 18 negatives
Want to review the keyword list, or publish as paused?`}</CodeBlock>
                </div>

                <div>
                  <h3 className="font-semibold text-foreground mb-2">
                    Auditing performance
                  </h3>
                  <CodeBlock>{`You: Show me my worst-performing ad groups this week.

Upfixe: Across 4 active campaigns, 3 ad groups had >$40 spend
and 0 conversions in the last 7 days:
  1. "Generic Tools" – $87, 142 clicks, 0 conv
  2. "Brand Lookups" – $52, 89 clicks, 0 conv
  3. "Discount Hunters" – $44, 67 clicks, 0 conv
Want me to pause them, or add negatives first?`}</CodeBlock>
                </div>

                <div>
                  <h3 className="font-semibold text-foreground mb-2">
                    Cleaning up spend leaks
                  </h3>
                  <CodeBlock>{`You: Pause anything spending over $30 with no conversions in 14 days.

Upfixe: Found 2 matches:
  • "Cheap Plumbing Hacks" — $58 spent, 0 conv
  • "Free Quote Requests" — $41 spent, 0 conv
Confirm pause? (yes / no)`}</CodeBlock>
                </div>
              </div>
            </Section>

            <Section id="one-click" title="One-click install">
              <p>
                Skip the manual setup — open the install flow directly in your
                AI client. You'll be prompted to authorise Upfixe (or to sign
                up if you don't have an account yet).
              </p>
              <div className="flex flex-col sm:flex-row gap-3 not-prose">
                <Button size="lg" asChild>
                  <a
                    href={CLAUDE_INSTALL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      trackEvent("mcp_install_claude_click", { source: "docs_end" })
                    }
                  >
                    Install in Claude
                    <ExternalLink className="ml-1 h-4 w-4" />
                  </a>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <a
                    href={CHATGPT_INSTALL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      trackEvent("mcp_install_chatgpt_click", { source: "docs_end" })
                    }
                  >
                    Install in ChatGPT
                    <ExternalLink className="ml-1 h-4 w-4" />
                  </a>
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                ChatGPT's connector settings open to the general page — paste{" "}
                <code className="px-1.5 py-0.5 rounded bg-muted text-xs font-mono">
                  https://app.upfixe.com/mcp
                </code>{" "}
                when prompted. Requires ChatGPT Business, Enterprise or Edu for
                write access. Claude users get full access on any plan.
              </p>
            </Section>

            <Section id="troubleshooting" title="Troubleshooting">
              <div>
                <h3 className="font-semibold text-foreground mb-2">
                  Tools don't appear after install
                </h3>
                <ul className="list-disc pl-5 space-y-2">
                  <li>
                    <strong>Claude Desktop:</strong> Make sure you fully quit
                    and restarted the app (not just closed the window). On
                    macOS: Cmd+Q. On Windows: right-click tray icon → Quit.
                    Config changes only apply on full restart.
                  </li>
                  <li>
                    <strong>Verify config file path:</strong>
                    <ul className="list-disc pl-5 mt-1 space-y-1">
                      <li>
                        macOS:{" "}
                        <code className="px-1.5 py-0.5 rounded bg-muted text-xs font-mono">
                          ~/Library/Application Support/Claude/claude_desktop_config.json
                        </code>
                      </li>
                      <li>
                        Windows:{" "}
                        <code className="px-1.5 py-0.5 rounded bg-muted text-xs font-mono">
                          %APPDATA%\Claude\claude_desktop_config.json
                        </code>
                      </li>
                    </ul>
                  </li>
                  <li>
                    <strong>Check JSON syntax:</strong> trailing commas, missing
                    quotes, or malformed brackets will silently break the entire
                    config file. Paste it into{" "}
                    <a
                      href="https://jsonlint.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      jsonlint.com
                    </a>{" "}
                    to validate.
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-2">
                  OAuth popup doesn't close / auth fails
                </h3>
                <ul className="list-disc pl-5 space-y-2">
                  <li>
                    Disable popup blocker for{" "}
                    <code className="px-1.5 py-0.5 rounded bg-muted text-xs font-mono">
                      app.upfixe.com
                    </code>{" "}
                    and try again.
                  </li>
                  <li>
                    If using a corporate browser, try the flow in a personal
                    Chrome/Firefox window — some browser security policies block
                    the redirect.
                  </li>
                  <li>
                    Clear cookies for{" "}
                    <code className="px-1.5 py-0.5 rounded bg-muted text-xs font-mono">
                      app.upfixe.com
                    </code>{" "}
                    and retry.
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-2">
                  "No Google Ads account connected" error on tool calls
                </h3>
                <ul className="list-disc pl-5 space-y-2">
                  <li>
                    You need to connect your Google Ads account separately from
                    the MCP OAuth. Log into{" "}
                    <a
                      href="https://app.upfixe.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      app.upfixe.com
                    </a>
                    , go to Settings → Connections → Connect Google Ads.
                  </li>
                  <li>
                    If you have multiple Google Ads accounts (e.g., agency
                    MCC), you'll be asked to pick which one to use.
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-2">
                  How do I revoke access?
                </h3>
                <p>Two ways:</p>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>
                    <strong>From Upfixe:</strong> Log into{" "}
                    <a
                      href="https://app.upfixe.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      app.upfixe.com
                    </a>{" "}
                    → Settings → MCP Connections → Revoke
                  </li>
                  <li>
                    <strong>From Google:</strong> Visit{" "}
                    <a
                      href="https://myaccount.google.com/permissions"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      myaccount.google.com/permissions
                    </a>
                    , find Upfixe, click Remove access.
                  </li>
                </ol>
                <p className="text-sm text-muted-foreground">
                  Both methods immediately invalidate Upfixe's access to your
                  Google Ads account.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-2">
                  Still stuck?
                </h3>
                <p>
                  Email{" "}
                  <a
                    href="mailto:george@upfixe.com"
                    className="text-primary hover:underline font-semibold"
                  >
                    george@upfixe.com
                  </a>{" "}
                  with a description of what you tried and any error messages.
                  We respond within one business day (EU hours).
                </p>
              </div>
            </Section>

            <Section id="data-handling" title="Data handling">
              <div>
                <h3 className="font-semibold text-foreground mb-3">
                  What we store on our servers
                </h3>
                <div className="rounded-lg border border-border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 font-semibold text-foreground">
                          Data
                        </th>
                        <th className="text-left p-3 font-semibold text-foreground">
                          Retention
                        </th>
                        <th className="text-left p-3 font-semibold text-foreground">
                          Purpose
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-background">
                        <td className="p-3">User account (email, name, billing)</td>
                        <td className="p-3 text-muted-foreground">Until account deletion</td>
                        <td className="p-3 text-muted-foreground">Authentication, billing</td>
                      </tr>
                      <tr className="bg-muted/20">
                        <td className="p-3">Google Ads OAuth refresh tokens</td>
                        <td className="p-3 text-muted-foreground">Until you revoke</td>
                        <td className="p-3 text-muted-foreground">
                          Re-authenticating to Google Ads when tokens expire
                        </td>
                      </tr>
                      <tr className="bg-background">
                        <td className="p-3">Value-prop documents (from homepage scrape)</td>
                        <td className="p-3 text-muted-foreground">30 days, then re-fetched</td>
                        <td className="p-3 text-muted-foreground">
                          Caching to avoid re-scraping on every campaign creation
                        </td>
                      </tr>
                      <tr className="bg-muted/20">
                        <td className="p-3">Landing-page documents (from URL scrape)</td>
                        <td className="p-3 text-muted-foreground">7 days, then re-fetched</td>
                        <td className="p-3 text-muted-foreground">Same as above</td>
                      </tr>
                      <tr className="bg-background">
                        <td className="p-3">Brand records</td>
                        <td className="p-3 text-muted-foreground">Until you delete</td>
                        <td className="p-3 text-muted-foreground">Multi-brand campaign organization</td>
                      </tr>
                      <tr className="bg-muted/20">
                        <td className="p-3">Campaign drafts</td>
                        <td className="p-3 text-muted-foreground">Until you delete or publish</td>
                        <td className="p-3 text-muted-foreground">Lets you iterate before going live</td>
                      </tr>
                      <tr className="bg-background">
                        <td className="p-3">MCP tool call audit log</td>
                        <td className="p-3 text-muted-foreground">30 days</td>
                        <td className="p-3 text-muted-foreground">Security and debugging</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-2">
                  What we send to OpenAI
                </h3>
                <p>
                  Upfixe uses OpenAI's GPT-4o server-side to extract structured
                  value propositions from scraped website HTML. The specific
                  data sent:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    HTML/text content of URLs you explicitly ask us to analyze
                    (homepages, landing pages)
                  </li>
                  <li>
                    The text output from that extraction (the structured
                    value-prop doc)
                  </li>
                </ul>
                <p>
                  What we <strong>do not</strong> send to OpenAI:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Your Google Ads OAuth tokens or any Google credentials</li>
                  <li>Your campaign performance data or Google Ads account contents</li>
                  <li>Your billing information</li>
                  <li>Other users' data</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-2">
                  What we send to Google
                </h3>
                <p>
                  All Google Ads API calls (Keyword Planner queries, campaign
                  create/update/pause, performance reporting) are made using
                  your own connected Google Ads account, authenticated via your
                  own OAuth grant. Upfixe does not pool data across customers
                  when querying Google.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-2">
                  OAuth scopes requested
                </h3>
                <p>
                  Upfixe requests one Google scope only:{" "}
                  <code className="px-1.5 py-0.5 rounded bg-muted text-xs font-mono">
                    https://www.googleapis.com/auth/adwords
                  </code>{" "}
                  — read and write access to your Google Ads campaigns. No
                  access to Gmail, Drive, Calendar, YouTube, or any other
                  Google service.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-2">
                  Hosting and security
                </h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>All data transmission over HTTPS/TLS</li>
                  <li>
                    Google Ads OAuth tokens stored encrypted at rest in our
                    US-hosted Supabase database (us-west-1), with row-level
                    security and access restricted to the authenticated user
                  </li>
                  <li>
                    Application hosted on Railway; data stored in Supabase
                    (US region — us-west-1). For EU/UK users, transfers of
                    personal data to the US are governed by Standard
                    Contractual Clauses
                  </li>
                  <li>SOC 2 / ISO 27001: not yet certified</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-2">Deletion</h3>
                <p>
                  Delete your account in Settings → Account → Delete account,
                  or email{" "}
                  <a
                    href="mailto:george@upfixe.com"
                    className="text-primary hover:underline"
                  >
                    george@upfixe.com
                  </a>
                  . All stored data including OAuth tokens and audit logs are
                  purged within 30 days. Cached value-prop and landing-page
                  documents are also purged immediately.
                </p>
              </div>

              <p className="text-sm text-muted-foreground">
                Full legal detail is in our{" "}
                <Link to="/privacy" className="text-primary hover:underline">
                  Privacy Policy
                </Link>{" "}
                and{" "}
                <Link to="/terms" className="text-primary hover:underline">
                  Terms of Service
                </Link>
                .
              </p>
            </Section>

            <Section id="also-listed-on" title="Also listed on">
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <a
                    href="https://smithery.ai/servers/george-mvur/upfixe-ads"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Smithery
                  </a>{" "}
                  — official Smithery directory
                </li>
                <li>
                  <a
                    href="https://mcp.so/server/upfixe-ads/George%20Woodworth"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    mcp.so
                  </a>{" "}
                  — community MCP directory
                </li>
                <li>mcpservers.org (pending review)</li>
              </ul>
            </Section>

            <div className="pt-8 border-t border-border">
              <p className="text-muted-foreground">
                Need help? Email{" "}
                <a
                  href="mailto:george@upfixe.com"
                  className="text-primary hover:underline"
                >
                  george@upfixe.com
                </a>{" "}
                or{" "}
                <Link to="/mcp" className="text-primary hover:underline">
                  start a free trial
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DocsMcp;
